import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { commonErrorResponses, requiredPayloadEnvelope } from '../envelope.js';
/** AdminUser.role의 닫힌 집합이다. 타입과 Zod는 이 배열에서만 파생한다. */
export const ADMIN_USER_ROLES = ['super_admin', 'admin'];
/** AdminUser.role 요청·응답을 검증한다. */
export const adminUserRoleSchema = z.enum(ADMIN_USER_ROLES);
/** admin-api 직원 계정 한 행의 HTTP 표현이다. `super_admin`은 presetIds와 무관하게 전체 권한을 가진다. */
const adminUserSchema = z.object({
    /** AdminUser 행의 기본 키다. */
    id: z.string(),
    /** 로그인·OTP 발송에 쓰는 이메일 주소다. */
    email: z.string(),
    /** 콘솔에 표시하는 직원 이름이다. */
    name: z.string(),
    /** `super_admin` 또는 `admin`이다. `admin`만 presetIds로 세부 권한을 받는다. */
    role: adminUserRoleSchema,
    /** `ADMIN_PRESET_CATALOG`의 id 목록이다. `admin` 역할일 때만 의미가 있고, 그 외에는 빈 배열이다. */
    presetIds: z.array(z.string()),
    /** 초기 임시 비밀번호를 아직 바꾸지 않았으면 true다. */
    isTempPassword: z.boolean(),
    /** 비활성화 시각이다. null이면 활성 계정이다. */
    disabledAt: z.union([z.string(), z.null(), z.date()]),
    /** 계정이 만들어진 시각이다. */
    createdAt: z.union([z.string(), z.date()]),
});
const c = initContract();
/** admin-api — 플랫폼 직원(AdminUser) 계정 CRUD·비활성화. `super_admin` 전용 시스템 라우트다. */
export const adminUsersContract = c.router({
    /** 등록된 직원 계정 목록을 조회한다. */
    list: {
        method: 'GET',
        path: '/system/admin-users',
        responses: {
            200: requiredPayloadEnvelope(z.array(adminUserSchema)),
            ...commonErrorResponses,
        },
    },
    /** 새 직원 계정을 만든다. 임시 비밀번호가 함께 발급된다. */
    create: {
        method: 'POST',
        path: '/system/admin-users',
        body: z.object({
            /** 로그인 이메일이다. 생략하면 서버가 거절한다. */
            email: z.string().optional(),
            /** 표시 이름이다. */
            name: z.string().optional(),
            /** `super_admin` 또는 `admin`이다. */
            role: z.string().optional(),
            /** `admin` 역할에 부여할 권한 프리셋 id 목록이다. */
            presetIds: z.array(z.string()).optional(),
        }),
        responses: {
            200: requiredPayloadEnvelope(adminUserSchema),
            ...commonErrorResponses,
        },
    },
    /** 직원 계정의 역할을 `admin`과 `super_admin` 사이에서 바꾼다. */
    updateRole: {
        method: 'PATCH',
        path: '/system/admin-users/:id/role',
        pathParams: z.object({
            /** 역할을 바꿀 AdminUser id다. */
            id: z.string(),
        }),
        body: z.object({
            /** 부여할 역할이다. */
            role: adminUserRoleSchema,
        }),
        responses: {
            200: requiredPayloadEnvelope(adminUserSchema),
            ...commonErrorResponses,
        },
    },
    /** 직원 계정을 비활성화한다. disabledAt이 채워진다. */
    deactivate: {
        method: 'PATCH',
        path: '/system/admin-users/:id/deactivate',
        pathParams: z.object({
            /** 비활성화할 AdminUser id다. */
            id: z.string(),
        }),
        body: c.noBody(),
        responses: {
            200: requiredPayloadEnvelope(adminUserSchema),
            ...commonErrorResponses,
        },
    },
    /** 정지된 직원 계정을 다시 활성화한다. 폐기된 세션은 복원하지 않는다. */
    reactivate: {
        method: 'PATCH',
        path: '/system/admin-users/:id/reactivate',
        pathParams: z.object({
            /** 재활성화할 AdminUser id다. */
            id: z.string(),
        }),
        body: c.noBody(),
        responses: {
            200: requiredPayloadEnvelope(adminUserSchema),
            ...commonErrorResponses,
        },
    },
});
export { adminUserSchema };
//# sourceMappingURL=admin-users.contract.js.map